import React, { useEffect, useRef, useState, useCallback } from "react";

interface TimelineProps {
  audioUrl: string;
  isPlaying: boolean;
  onPlayPause: () => void;
}

const Timeline: React.FC<TimelineProps> = ({ audioUrl, isPlaying, onPlayPause }) => {
  const audioRef = useRef<HTMLAudioElement>(new Audio(audioUrl));
  const timelineRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Sync play/pause state
  useEffect(() => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }

    const handleAudioEnded = () => {
      onPlayPause();
      setCurrentTime(duration);
      setProgress(100);
    };

    audio.addEventListener("ended", handleAudioEnded);

    // Cleanup on unmount
    return () => {
      audio.pause();
      audio.removeEventListener("ended", handleAudioEnded);
    };
  }, [isPlaying, audioUrl, duration, onPlayPause]);


  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!isDragging && audio.duration) {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration);
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  }, [isDragging]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", updateProgress);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", updateProgress);
    };
  }, [updateProgress]);

  // Seek on click
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const timeline = timelineRef.current;
    if (timeline && audio.duration) {
      const rect = timeline.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * audio.duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress((newTime / audio.duration) * 100);
    }
  };

  // Draggable functionality
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      const audio = audioRef.current;
      const timeline = timelineRef.current;
      if (timeline && audio.duration) {
        const rect = timeline.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const newProgress = Math.max(0, Math.min(100, (mouseX / rect.width) * 100));
        const newTime = (newProgress / 100) * audio.duration;
        setCurrentTime(newTime);
        setProgress(newProgress);
      }
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      const audio = audioRef.current;
      audio.currentTime = currentTime;
    }
  }, [isDragging, currentTime]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Accessibility: Keyboard controls for seeking
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio.duration) return;

    let newTime = currentTime;
    const seekStep = 5; // Seek by 5 seconds

    switch (e.key) {
      case "ArrowLeft":
        newTime = Math.max(0, currentTime - seekStep);
        e.preventDefault();
        break;
      case "ArrowRight":
        newTime = Math.min(audio.duration, currentTime + seekStep);
        e.preventDefault();
        break;
      case "Home": // Seek to start
        newTime = 0;
        e.preventDefault();
        break;
      case "End": // Seek to end
        newTime = audio.duration;
        e.preventDefault();
        break;
      default:
        return;
    }

    audio.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress((newTime / audio.duration) * 100);
  };


  return (
    <div className="timeline-container">
      <div className="time-display">
        <span className="current-time">{formatTime(currentTime)}</span>
        <span className="time-separator"> / </span>
        <span className="total-time">{formatTime(duration)}</span>
      </div>
      <div
        className="timeline"
        ref={timelineRef}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="slider"
        aria-label="Audio playback progress"
        aria-valuenow={Math.floor(currentTime)}
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration)}
        aria-valuetext={`Current time ${formatTime(currentTime)} of ${formatTime(duration)}`}
        style={{
          width: "100%",
          height: "8px",
          backgroundColor: "#ccc",
          marginTop: "10px",
          cursor: "pointer",
          position: "relative",
          borderRadius: "4px",
        }}
      >
        <div
          className="timeline-progress"
          style={{
            width: `${progress}%`,
            height: "100%",
            backgroundColor: "#5030E5",
            borderRadius: "4px",
            transition: isDragging ? "none" : "width 0.1s linear",
          }}
        ></div>
        <div
          className="scrubber-handle"
          onMouseDown={handleMouseDown}
          style={{
            position: "absolute",
            left: `${progress}%`, // Position based on progress
            top: "50%",
            transform: "translate(-50%, -50%)", // Center the handle
            width: "16px",
            height: "16px",
            backgroundColor: "#5030E5",
            borderRadius: "50%",
            cursor: "grab",
            boxShadow: "0 0 5px rgba(0,0,0,0.3)",
            transition: isDragging ? "none" : "left 0.1s linear", // Disable transition during drag
          }}
        ></div>
      </div>
    </div>
  );
};

export default Timeline;
